import React, { useRef, useMemo, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact, { HighchartsReactProps } from 'highcharts-react-official';
import { useResizeListener } from '../../Hooks';
import { debounce } from 'lodash';

interface StoneXChartProps extends HighchartsReactProps {
  width?: string | number;
  height?: string | number;
  onChartReady?: (chart: Highcharts.Chart) => void;
}

/**
 * StoneXChart is a wrapper around HighchartsReact that auto-resizes the chart when its parent div is resized.
 * It accepts all HighchartsReact props, plus optional width and height for the parent div.
 */
export default function StoneXChart(props: StoneXChartProps) {
  const { width = '100%', height = '100%', onChartReady, ...highchartsProps } = props;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const chartContainerDimensions = useResizeListener(chartContainerRef);

  const chartSizeUpdate = useMemo(
    () =>
      debounce(() => {
        chartRef.current?.chart.setSize(chartContainerDimensions.x, chartContainerDimensions.y);
      }, 50),
    [chartContainerDimensions],
  );

  useEffect(() => {
    
    if (chartContainerDimensions.x == 0 && chartContainerDimensions.y == 0) {
      return;
    }

    chartSizeUpdate();

    return () => {
      chartSizeUpdate.cancel();
    };
  }, [chartContainerDimensions]);

  useEffect(() => {
    if (chartRef.current?.chart && onChartReady) {
      onChartReady(chartRef.current.chart);
    }
  }, [onChartReady]);

  return (
    <div ref={chartContainerRef} style={{ width, height, position: 'relative', overflow: 'hidden', resize: 'both', padding: 0, margin: 0 }}>
      <HighchartsReact ref={chartRef} highcharts={Highcharts} {...highchartsProps} />
    </div>
  );
}
